import React, { JSX, useEffect, useRef, useState } from 'react';
import 'azure-maps-control/dist/atlas.min.css';

type LMapProps = {
  children?: React.ReactNode;
  center?: [number, number] | null;
  zoom?: number | null;
  bounds?: [number, number, number, number] | null;
  maxZoom?: number;
  boundsMaxZoom?: number;
  minZoom?: number;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  fullscreenControl?: boolean;
  style?: string;
};

type ChildWithMapProp = React.ReactElement<{ map?: any }>;

const DEFAULT_CENTER: [number, number] = [-89.5889864, 40.6936488];

const LMap = ({
  children,
  center,
  zoom,
  bounds,
  maxZoom = 17,
  boundsMaxZoom = 8,
  minZoom = 1,
  zoomControl = true,
  mapTypeControl = false,
  fullscreenControl = false,
  style = 'road_shaded_relief',
  }: LMapProps): JSX.Element => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);

  const [atlasLib, setAtlasLib] = useState<any>(null);
  const [map, setMap] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);

  const zoomControlRef = useRef<any>(null);
  const styleControlRef = useRef<any>(null);
  const fullscreenControlRef = useRef<any>(null);

  useEffect(() => {
    let mounted = true;

    const loadAtlas = async () => {
      if (typeof window === 'undefined') return;

      try {
        const atlasModule = await import('azure-maps-control');
        const atlas = atlasModule.default || atlasModule;

        if (!mounted) return;

        setAtlasLib(atlas);
      } catch (error) {
        console.error('Failed to load Azure Maps SDK:', error);
      }
    };

    loadAtlas();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!atlasLib || !mapRef.current || mapInstanceRef.current) {
      return undefined;
    }

    const subscriptionKey = process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

    if (!subscriptionKey) {
      console.error(
        'Missing environment variable.'
      );
      return undefined;
    }

    try {
      atlasLib.setSubscriptionKey(subscriptionKey);
      atlasLib.setAuthenticationOptions({
        authType: atlasLib.AuthenticationType.subscriptionKey,
        subscriptionKey,
      });

      const azureMap = new atlasLib.Map(mapRef.current, {
        authOptions: {
          authType: atlasLib.AuthenticationType.subscriptionKey,
          subscriptionKey,
        },
        center: center ?? DEFAULT_CENTER,
        zoom: zoom ?? boundsMaxZoom,
        maxZoom,
        minZoom,
        style,
        view: 'Auto',
        language: 'en-US',
      });

      mapInstanceRef.current = azureMap;
      setMap(azureMap);

      const handleReady = () => {
        setIsReady(true);
      };

      const handleError = (e: any) => {
        console.error('Azure Maps error:', e);
      };

      azureMap.events.add('ready', handleReady);
      azureMap.events.add('error', handleError);

      return () => {
        try {
          azureMap.events.remove('ready', handleReady);
          azureMap.events.remove('error', handleError);

          if (zoomControlRef.current) {
            azureMap.controls.remove(zoomControlRef.current);
            zoomControlRef.current = null;
          }

          if (styleControlRef.current) {
            azureMap.controls.remove(styleControlRef.current);
            styleControlRef.current = null;
          }

          if (fullscreenControlRef.current) {
            azureMap.controls.remove(fullscreenControlRef.current);
            fullscreenControlRef.current = null;
          }

          azureMap.dispose();
          mapInstanceRef.current = null;
          setMap(null);
          setIsReady(false);
        } catch (error) {
          console.error('Azure map cleanup failed:', error);
        }
      };
    } catch (error) {
      console.error('Azure map initialization failed:', error);
      return undefined;
    }
  }, [atlasLib]);

  useEffect(() => {
    if (!atlasLib || !map || !isReady) return;

    if (zoomControlRef.current) {
      map.controls.remove(zoomControlRef.current);
      zoomControlRef.current = null;
    }

    if (styleControlRef.current) {
      map.controls.remove(styleControlRef.current);
      styleControlRef.current = null;
    }

    if (fullscreenControlRef.current) {
      map.controls.remove(fullscreenControlRef.current);
      fullscreenControlRef.current = null;
    }

    if (zoomControl) {
      zoomControlRef.current = new atlasLib.control.ZoomControl();
      map.controls.add(zoomControlRef.current, {
        position: atlasLib.ControlPosition.TopRight,
      });
    }

    if (mapTypeControl) {
      styleControlRef.current = new atlasLib.control.StyleControl();
      map.controls.add(styleControlRef.current, {
        position: atlasLib.ControlPosition.TopRight,
      });
    }

    if (fullscreenControl) {
      fullscreenControlRef.current = new atlasLib.control.FullscreenControl();
      map.controls.add(fullscreenControlRef.current, {
        position: atlasLib.ControlPosition.TopRight,
      });
    }
  }, [
    atlasLib,
    map,
    isReady,
    zoomControl,
    mapTypeControl,
    fullscreenControl,
  ]);

  useEffect(() => {
    if (!map || !isReady) return;

    map.setCamera({
      minZoom,
      maxZoom,
    });

    if (bounds) {
      map.setCamera({
        bounds,
        padding: 40,
      });

      return;
    }

    if (center) {
      map.setCamera({
        center,
        zoom: zoom ?? boundsMaxZoom,
      });

      return;
    }

    map.setCamera({
      center: DEFAULT_CENTER,
      zoom: zoom ?? boundsMaxZoom,
    });
  }, [map, isReady, center, zoom, bounds, boundsMaxZoom, minZoom, maxZoom]);

  return (
    <div ref={mapRef} className="map-container">
      {map &&
        isReady &&
        React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return null;

          return React.cloneElement(child as ChildWithMapProp, {
            map,
          });
        })}
    </div>
  );
};

export default LMap;